/**
 * Small pure helpers behind the device editor's build + hardware-identity
 * fields. Kept out of the component so the rules are testable without
 * rendering anything.
 *
 * NOTE what is deliberately NOT here any more: a function deriving the build
 * from the screen type. That was wrong — a K10 and a Touch Dynamic Vector are
 * both dual-screen Android terminals and take DIFFERENT builds, because the K10
 * carries built-in peripherals whose vendor SDKs the Vector has no use for. No
 * screen type can answer which of the two a device takes, so the build is
 * chosen from `POS_BUILD_TARGETS` and screen type is left to do its own job.
 */

import { POS_BUILD_TARGETS } from '@platform/shared-types';

export interface BuildTargetOption {
  id: string;
  displayName: string;
  supportedDevices: readonly string[];
}

/**
 * A FALLBACK THAT STILL WORKS.
 *
 * Every runtime shared-types read here is guarded, and the FIRST version of this
 * guard degraded to `[]` — which rendered the Build picker with no options at
 * all and "No matches" under the search box. A guard that turns a crash into an
 * empty control has not helped anybody; it just moves the failure somewhere
 * quieter, and an operator reads "No matches" as "there are no builds", which is
 * a lie. Same lesson as `INTEGRATIONS_FALLBACK` and `FORM_FACTOR_LABEL_FALLBACK`
 * in the two files either side of this one.
 *
 * The local copy is the same list. A stale Vite pre-bundle now costs nothing
 * visible. Keep it in step by hand — the shared list is the source of truth and
 * this is only ever reached when that one has not loaded.
 */
const FALLBACK: readonly BuildTargetOption[] = [
  {
    id: 'android_dual_screen',
    displayName: 'RUBY-ANDROID-POS-BUILD',
    supportedDevices: ['TC-TOUCH-K10', 'TOUCH-DYNAMIC-VECTOR', 'TC-TOUCH-A10', 'Any Android POS'],
  },
];

/** Every build on offer. Never empty — see `FALLBACK`. */
export function buildTargets(): readonly BuildTargetOption[] {
  try {
    if (Array.isArray(POS_BUILD_TARGETS) && POS_BUILD_TARGETS.length) {
      return POS_BUILD_TARGETS as readonly BuildTargetOption[];
    }
  } catch { /* fall through */ }
  return FALLBACK;
}

/**
 * The options to draw: every build, PLUS whatever this model already carries.
 * A stored target retired from the list must never vanish from the picker — the
 * device would read as having NO build configured, which is a different and much
 * louder thing than "on an old build".
 */
export function buildTargetOptions(current?: string | null): BuildTargetOption[] {
  const known = [...buildTargets()];
  if (current && !known.some((t) => t.id === current)) {
    known.push({ id: current, displayName: current, supportedDevices: [] });
  }
  return known;
}

/** The devices a build runs on, for the line under the picker. */
export function devicesFor(id?: string | null): readonly string[] {
  if (!id) return [];
  return buildTargets().find((t) => t.id === id)?.supportedDevices ?? [];
}

/**
 * One line of typed text → the stored `hardwareIds` list.
 *
 * Splits on commas, semicolons and line breaks — NOT on spaces. A model string
 * can itself contain one: the SUNMI T3 Pro reports `T3 PRO`, and splitting that
 * into `T3` + `PRO` would make the row claim every T3 80mm (which reports `T3`).
 * Trims, drops blanks and de-duplicates case-insensitively while KEEPING the
 * first spelling typed: the comparison itself ignores case, so storing both
 * `TC-TOUCH-K10` and `tc-touch-k10` would add a row that can never match
 * anything the other does not already match.
 *
 * Returns `undefined`, not `[]`, for an empty line — absent means "nothing to
 * check against", and an empty array would be a stored answer meaning the same
 * thing in a way every reader then has to handle twice.
 */
export function parseHardwareIds(text: string): string[] | undefined {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of (text || '').split(/[,;\r\n]+/)) {
    const v = raw.trim();
    if (!v) continue;
    const k = v.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(v);
  }
  return out.length ? out : undefined;
}
